import React, {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {DriveConnectModal} from './DriveConnectModal';
import {autoSyncLabel, connectionAutoSyncs, connectionAwaitsFirstRead} from '../ia_booster/feed/sourcesModel';

// Vista "Conexiones": sumar ARCHIVOS de Google Drive a la memoria del cliente. El alta la maneja el
// wizard `DriveConnectModal` (Picker multi-archivo -> tabla de admisión -> crear). Acá se lista /
// sincroniza a demanda / reconecta (re-pickear) / desconecta. Además de a demanda, cada archivo se
// relee solo todos los días a las 5 (cron del backend), y la fila lo dice.

// Mientras una conexión recién creada espera su primera lectura, se vuelve a preguntar cada tanto:
// la lista se pedía UNA vez al montar y al cerrar el Picker, cuando el sync todavía no terminó, así
// que la fila decía «Sin sincronizar todavía» toda la visita (card pH4QPmhG).
const FIRST_READ_POLL_MS = 5000;
// Vueltas SEGUIDAS que pueden fallar antes de dejar de preguntar: una respuesta mala no corta el
// seguimiento (mismo criterio que la biblioteca de la 2.1 y que `FeedActContent`).
const MAX_POLL_RETRIES = 3;
const STATUS_CLASS = {
    ACTIVE: '-active',
    PAUSED: '-paused',
    ERROR: '-error',
    REVOKED: '-revoked',
};
const NEEDS_RECONNECT = new Set(['ERROR', 'REVOKED']);

// `openConnectToken`: la segunda puerta al Picker. La primera es `?connect=drive` en la URL (el menú
// "+ Agregar"); ésta la usa el Recorrido, donde esta vista vive dentro de un panel lateral y quien
// pide "conectar Drive" es un botón de la misma página — sin navegar, o sea sin query que leer. Es
// un contador: cada vez que sube, el Picker se abre (si Drive está configurado).
export function MemoryConnectionsView({clientId, openConnectToken = 0}) {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [config, setConfig] = useState(null);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connectModalOpen, setConnectModalOpen] = useState(false);
    const [syncingId, setSyncingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Cada pedido lleva su número y sólo el último manda: una vuelta del sondeo que se cruza con
    // «Sincronizar» no pisa la respuesta nueva con la vieja.
    const seqRef = useRef(0);
    const failuresRef = useRef(0);
    // Sube después de CADA pedido, bien o mal: es lo que vuelve a agendar el sondeo. Colgado de
    // `connections`, una respuesta mala no cambiaba nada y la fila quedaba en «Sincronizando…».
    const [pollRound, setPollRound] = useState(0);

    const refresh = () => {
        const seq = seqRef.current + 1;
        seqRef.current = seq;
        return ClientMemoryApi.listConnections(clientId)
            .then((data) => {
                if (seq !== seqRef.current) return;
                if (data && Array.isArray(data.connections)) {
                    failuresRef.current = 0;
                    setConnections(data.connections);
                } else {
                    failuresRef.current += 1;
                }
            })
            .catch(() => {
                if (seq === seqRef.current) failuresRef.current += 1;
            })
            .finally(() => setPollRound((round) => round + 1));
    };

    // El sondeo de la primera lectura. Lo dispara el estado (no quien conectó), así que también
    // cubre entrar a la pantalla con una conexión recién creada en otra pestaña.
    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;
    const awaitingFirstRead = connections.some((conn) => connectionAwaitsFirstRead(conn));
    useEffect(() => {
        if (!awaitingFirstRead || failuresRef.current > MAX_POLL_RETRIES) return undefined;
        const timer = setTimeout(() => refreshRef.current(), FIRST_READ_POLL_MS);
        return () => clearTimeout(timer);
    }, [awaitingFirstRead, pollRound]);

    useEffect(() => {
        let active = true;
        Promise.all([ClientMemoryApi.getDriveConfig(), ClientMemoryApi.listConnections(clientId)])
            .then(([cfg, list]) => {
                if (!active) return;
                setConfig(cfg || {configured: false});
                if (list && Array.isArray(list.connections)) setConnections(list.connections);
            })
            .catch(() => active && setConfig({configured: false}))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [clientId]);

    // El item "Archivos de Drive" del menú "+ Agregar" navega acá con `?connect=drive` para que el
    // Picker se abra solo (antes el link sólo aterrizaba en Fuentes: si el usuario ya estaba parado
    // acá, el click no hacía nada visible — rage clicks). Espera a tener la config y consume el flag
    // (lo borra de la URL) para que un nuevo click del menú vuelva a dispararlo.
    useEffect(() => {
        if (searchParams.get('connect') !== 'drive') return;
        if (!config) return;
        if (config.configured) setConnectModalOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete('connect');
        setSearchParams(next, {replace: true});
    }, [searchParams, config, setSearchParams]);

    // El primer valor no abre nada: sería abrir el Picker por el solo hecho de montar la vista.
    const seenConnectToken = useRef(openConnectToken);
    useEffect(() => {
        if (openConnectToken === seenConnectToken.current) return;
        seenConnectToken.current = openConnectToken;
        if (config && config.configured) setConnectModalOpen(true);
    }, [openConnectToken, config]);

    const onConnected = () => {
        setConnectModalOpen(false);
        refresh();
    };

    const syncConnection = (connection) => {
        setSyncingId(connection.id);
        ClientMemoryApi.syncConnection(connection.id, clientId)
            .then((data) => {
                if (data && data.status === 'OK') toast.success(t('client_memory.connections.toast.synced'));
                else toast.error(t('client_memory.connections.toast.error'));
                return refresh();
            })
            .catch(() => toast.error(t('client_memory.connections.toast.error')))
            .finally(() => setSyncingId(null));
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        ClientMemoryApi.deleteConnection(deleteTarget.id, clientId)
            .then((data) => {
                if (data && data.status === 'OK') toast.success(t('client_memory.connections.toast.disconnected'));
                else toast.error(t('client_memory.connections.toast.error'));
                return refresh();
            })
            .catch(() => toast.error(t('client_memory.connections.toast.error')))
            .finally(() => {
                setDeleting(false);
                setDeleteTarget(null);
            });
    };

    if (loading) {
        return (
            <div className="box-loader">
                <Spinner color="positive"/>
            </div>
        );
    }

    if (config && !config.configured) {
        return (
            <div className="cmem-empty">
                <FaIcon iconName="file-lines" className="cmem-empty-icon"/>
                <h3>{t('client_memory.connections.not_configured_title')}</h3>
                <p>{t('client_memory.connections.not_configured_subtitle')}</p>
            </div>
        );
    }

    const connectBtn = (
        <button type="button" className="btn -alt" onClick={() => setConnectModalOpen(true)}>
            {t('client_memory.connections.connect_button')}
        </button>
    );

    return (
        <div className="cmem-connections">
            <p className="cmem-connections-intro">{t('client_memory.connections.intro')}</p>

            {connections.length === 0 ? (
                <div className="cmem-empty">
                    <FaIcon iconName="file-lines" className="cmem-empty-icon"/>
                    <h3>{t('client_memory.connections.empty_title')}</h3>
                    <p>{t('client_memory.connections.empty_subtitle')}</p>
                    <div className="cmem-empty-actions">{connectBtn}</div>
                </div>
            ) : (
                <>
                    <div className="cmem-connections-actions">{connectBtn}</div>
                    <ul className="cmem-connection-list">
                        {connections.map((conn) => {
                            const needsReconnect = NEEDS_RECONNECT.has(conn.status);
                            return (
                                <li key={conn.id} className={`cmem-connection-card ${STATUS_CLASS[conn.status] || ''}`}>
                                    <FaIcon iconName="file-lines"/>
                                    <div className="cmem-connection-main">
                                        <span className="cmem-connection-name">{conn.external_label}</span>
                                        <span className="cmem-connection-meta">
                                            <span className={`cmem-connection-status ${STATUS_CLASS[conn.status] || ''}`}>
                                                {t(`client_memory.connections.status.${(conn.status || '').toLowerCase()}`,
                                                    {defaultValue: conn.status || ''})}
                                            </span>
                                            {' · '}
                                            {/* Activa y ya leída: la misma frase que la fila de la
                                                2.1. Recién creada: la primera lectura está en curso.
                                                Pausada, caída o trabada no se relee sola: ahí se dice
                                                lo que pasó, sin prometer nada. */}
                                            {(() => {
                                                if (connectionAwaitsFirstRead(conn)) {
                                                    return t('client_memory.connections.syncing');
                                                }
                                                if (connectionAutoSyncs(conn) && conn.last_synced_at) {
                                                    return autoSyncLabel(t, conn);
                                                }
                                                return conn.last_synced_at
                                                    ? t('client_memory.connections.last_synced', {
                                                        date: new Date(conn.last_synced_at).toLocaleString(
                                                            'es-AR', {dateStyle: 'short', timeStyle: 'short'}),
                                                        // La fecha es valor del sistema (no input de usuario); sin
                                                        // esto i18next escapa el "/" de la fecha a "&#x2F;".
                                                        interpolation: {escapeValue: false},
                                                    })
                                                    : t('client_memory.connections.never_synced');
                                            })()}
                                        </span>
                                        {needsReconnect && (
                                            <span className="cmem-connection-hint">
                                                {t('client_memory.connections.reconnect_hint')}
                                            </span>
                                        )}
                                        {conn.status === 'ERROR' && conn.last_sync_error && (
                                            <span className="cmem-connection-error">{conn.last_sync_error}</span>
                                        )}
                                    </div>
                                    <div className="cmem-connection-actions">
                                        {needsReconnect ? (
                                            <button type="button" className="btn -negative"
                                                    onClick={() => setConnectModalOpen(true)}>
                                                {t('client_memory.connections.reconnect')}
                                            </button>
                                        ) : (
                                            <button type="button" className="btn -negative"
                                                    disabled={syncingId === conn.id}
                                                    onClick={() => syncConnection(conn)}>
                                                {syncingId === conn.id
                                                    ? t('client_memory.connections.syncing')
                                                    : t('client_memory.connections.sync')}
                                            </button>
                                        )}
                                        <button type="button" className="btn -alt -negative"
                                                onClick={() => setDeleteTarget(conn)}>
                                            {t('client_memory.connections.disconnect')}
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}

            {config && config.configured && (
                <DriveConnectModal
                    isOpen={connectModalOpen}
                    onClose={() => setConnectModalOpen(false)}
                    clientId={clientId}
                    config={config}
                    onConnected={onConnected}
                />
            )}

            <YesNoModal
                skipRenderButton
                isOpen={!!deleteTarget}
                title={t('client_memory.connections.disconnect_confirm_title')}
                question={t('client_memory.connections.disconnect_confirm_question', {
                    name: deleteTarget ? deleteTarget.external_label : '',
                    // El nombre del archivo puede tener "&" o "/" → sin esto i18next los muestra como
                    // entidad HTML cruda (&amp; / &#x2F;).
                    interpolation: {escapeValue: false},
                })}
                isSaving={deleting}
                onYesAnswer={confirmDelete}
                onNoAnswer={() => setDeleteTarget(null)}
            />
        </div>
    );
}
